import { getHeaders } from './request'

export type RequestConfig<TData = unknown> = {
    url?: string
    method: 'GET' | 'PUT' | 'PATCH' | 'POST' | 'DELETE'
    params?: object
    data?: TData | FormData
    responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream'
    signal?: AbortSignal
    headers?: HeadersInit
}

export type ResponseConfig<TData = unknown> = {
    data: TData
    status: number
    statusText: string
}

export type ResponseErrorConfig<TError = unknown> = {
    error: TError
}

function buildQueryParams(params: Record<string, any>): string {
    const searchParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            // Append the key multiple times for each array element
            value.filter((x) => x).forEach((item) => searchParams.append(key, item))
        } else if (value !== undefined && value !== null) {
            // Standard single value
            searchParams.append(key, value.toString())
        }
    })

    const query = searchParams.toString()
    return query ? `?${query}` : ''
}

export const client =
    async (baseUrl: string) =>
    async <TData, TError = unknown, TVariables = unknown>(
        config: RequestConfig<TVariables>,
    ): Promise<ResponseConfig<TData>> => {
        const headers = getHeaders()

        const queryString = config.params ? buildQueryParams(config.params) : ''
        const url = `${baseUrl}${config.url || ''}${queryString}`

        const response = await fetch(url, {
            method: config.method.toUpperCase(),
            body: config.data ? JSON.stringify(config.data) : undefined,
            signal: config.signal,
            headers: {
                ...headers,
                ...config.headers,
            },
        })

        if (response.status === 204) {
            return {
                data: undefined as TData,
                status: response.status,
                statusText: response.statusText,
            }
        }

        const isText =
            response.headers.has('content-type') &&
            response.headers.get('content-type')?.startsWith('text/plain')

        let data: any
        try {
            data = isText ? await response.text() : await response.json()
        } catch (thrownError) {
            const e = new Error() as TError
            ;(e as any).error = {
                title: 'Response Parsing Error',
                error: { message: thrownError.toString() },
            }
            throw e
        }

        if (!response.ok) {
            const e = new Error(response.statusText) as TError
            ;(e as any).error = data
            throw e
        }

        return {
            data,
            status: response.status,
            statusText: response.statusText,
        }
    }

export default client
